/**
 * Komal OS - Vendor Directory: list.
 *
 * Lightweight, deliberately not the CRM's shape - a reference list of
 * vendors, filterable by what they supply, for "whom we can refer when an
 * additional vendor for a particular product is required" (2026-09-10
 * user request). Access is shared across CEO + the Design/Procurement/
 * Project/Production/Operations HODs (see app/routes/vendor_routes.py's
 * require_vendor_access) - everyone with access sees the same one list,
 * no per-owner scoping.
 */

import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Plus, Search, Truck, X, Upload, Camera } from 'lucide-react';
import { listVendors, createVendor, listSupplyItems } from '../../services/vendorService';
import useUIStore from '../../store/uiStore';

const CATEGORIES = ['Material Supplier', 'Equipment', 'Contractor', 'Services', 'Other'];
const emptyForm = () => ({ name: '', category: '', gstin: '', phone: '', email: '', address: '' });

export default function VendorsListPage() {
  const { showToast } = useUIStore();

  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [supplyItems, setSupplyItems] = useState([]);

  const [q, setQ] = useState('');
  const [supplyItemId, setSupplyItemId] = useState('');
  const [category, setCategory] = useState('');

  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res = await listVendors({ q: q || undefined, supply_item_id: supplyItemId || undefined, category: category || undefined });
      setVendors(res.data.vendors);
    } catch (err) {
      showToast(err.response?.data?.detail || 'Could not load vendors', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    listSupplyItems().then((res) => setSupplyItems(res.data.supply_items)).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const timer = setTimeout(load, 300);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q, supplyItemId, category]);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) {
      showToast('Vendor name is required', 'error');
      return;
    }
    setSaving(true);
    try {
      await createVendor(form);
      showToast('Vendor added', 'success');
      setCreating(false);
      setForm(emptyForm());
      load();
    } catch (err) {
      showToast(err.response?.data?.detail || 'Could not add vendor', 'error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen p-6">
      <div className="max-w-6xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-xl font-bold text-white">Vendors</h1>
            <p className="text-sm text-white/70 mt-0.5">Reference list — who to call when you need another supplier for something.</p>
          </div>
          <div className="flex items-center gap-2">
            <Link
              to="/vendors/scan"
              className="flex items-center gap-1.5 text-sm font-semibold text-white bg-white/10 hover:bg-white/20 border border-white/20 rounded-lg px-3.5 py-2"
            >
              <Camera size={15} /> Scan a card
            </Link>
            <Link
              to="/vendors/import"
              className="flex items-center gap-1.5 text-sm font-semibold text-white bg-white/10 hover:bg-white/20 border border-white/20 rounded-lg px-3.5 py-2"
            >
              <Upload size={15} /> Import list
            </Link>
            <button
              onClick={() => setCreating((v) => !v)}
              className="flex items-center gap-1.5 text-sm font-semibold text-white bg-komal-blue rounded-lg px-3.5 py-2"
            >
              <Plus size={15} /> New vendor
            </button>
          </div>
        </div>

        {creating && (
          <form onSubmit={handleCreate} className="glass-card p-5 mb-5 grid gap-3 sm:grid-cols-2">
            <div className="sm:col-span-2 flex items-center justify-between">
              <span className="text-sm font-semibold text-gray-800">New vendor</span>
              <button type="button" onClick={() => setCreating(false)} className="text-gray-400 hover:text-gray-600"><X size={16} /></button>
            </div>
            <input required placeholder="Vendor / company name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="border border-gray-300 rounded px-3 py-2 text-sm" />
            <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="border border-gray-300 rounded px-3 py-2 text-sm bg-white">
              <option value="">Category…</option>
              {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
            <input placeholder="GSTIN" value={form.gstin} onChange={(e) => setForm({ ...form, gstin: e.target.value })} className="border border-gray-300 rounded px-3 py-2 text-sm" />
            <input placeholder="Phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="border border-gray-300 rounded px-3 py-2 text-sm" />
            <input placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="border border-gray-300 rounded px-3 py-2 text-sm" />
            <input placeholder="Address" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} className="border border-gray-300 rounded px-3 py-2 text-sm" />
            <button disabled={saving} className="sm:col-span-2 text-sm font-semibold text-white bg-green-700 rounded px-3 py-2 disabled:opacity-60">
              {saving ? 'Saving…' : 'Add vendor'}
            </button>
          </form>
        )}

        <div className="flex flex-wrap gap-2 mb-4">
          <div className="relative flex-1 min-w-[200px]">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search vendor, GSTIN…"
              className="w-full pl-8 pr-3 py-2 text-sm rounded-lg border border-white/20 bg-white/10 text-white placeholder-white/50 focus:bg-white focus:text-gray-800 focus:placeholder-gray-400"
            />
          </div>
          <select value={supplyItemId} onChange={(e) => setSupplyItemId(e.target.value)} className="text-sm rounded-lg border border-white/20 bg-white/10 text-white px-3 py-2">
            <option value="" className="text-gray-800">Product: All</option>
            {supplyItems.map((s) => <option key={s.id} value={s.id} className="text-gray-800">{s.name}</option>)}
          </select>
          <select value={category} onChange={(e) => setCategory(e.target.value)} className="text-sm rounded-lg border border-white/20 bg-white/10 text-white px-3 py-2">
            <option value="" className="text-gray-800">Category: All</option>
            {CATEGORIES.map((c) => <option key={c} value={c} className="text-gray-800">{c}</option>)}
          </select>
        </div>

        <div className="glass-card overflow-x-auto">
          {loading ? (
            <p className="text-gray-500 text-sm p-5">Loading…</p>
          ) : vendors.length === 0 ? (
            <p className="text-gray-400 text-sm p-5 flex items-center gap-2"><Truck size={15} /> No vendors yet.</p>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[11px] uppercase tracking-wide text-gray-500 border-b border-gray-100">
                  <th className="px-4 py-2.5 font-semibold">Vendor</th>
                  <th className="px-4 py-2.5 font-semibold">Products supplied</th>
                  <th className="px-4 py-2.5 font-semibold">Category</th>
                  <th className="px-4 py-2.5 font-semibold">Contacts</th>
                  <th className="px-4 py-2.5 font-semibold">Last note</th>
                  <th className="px-4 py-2.5 font-semibold">Added by</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {vendors.map((v) => (
                  <tr key={v.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <Link to={`/vendors/${v.id}`} className="font-semibold text-komal-blue hover:underline">{v.name}</Link>
                      {v.address && <div className="text-xs text-gray-400">{v.address}</div>}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex flex-wrap gap-1">
                        {v.supply_items.length === 0 ? <span className="text-gray-300">—</span> : v.supply_items.map((s) => (
                          <span key={s.id} className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-purple-50 text-purple-700 border border-purple-200">{s.name}</span>
                        ))}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-gray-600">{v.category || '—'}</td>
                    <td className="px-4 py-3 text-gray-600">{v.contact_count}</td>
                    <td className="px-4 py-3 text-gray-400 text-xs">
                      {v.last_note_at ? new Date(v.last_note_at).toLocaleDateString() : 'No notes yet'}
                    </td>
                    <td className="px-4 py-3 text-gray-600">{v.created_by?.name || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}
